import React, { useState, useEffect } from "react";
import { useAppContext } from "components/context/AppContext";
import { Dialog, DialogTitle } from "@headlessui/react";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Pagination from "components/misc/Pagination";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import moment from "moment";
import { useAccounting } from "../hooks/useAccounting";
import { useFraccs } from "hooks/useFraccs";
import { useCatalogs } from "hooks/useCatalogs";
import AdminAccountingForm from "components/forms/AdminAccountingForm";
import AccountingFilters from "components/features/AccountingFilters";
import AccountingOperations from "components/features/AccountingOperations";
import AccountingReport from "components/features/AccountingReport";
import PeriodsTable from "components/tables/PeriodsTable";
import OperationsTable from "components/tables/OperationsTable";

const Container = tw.div`relative`;
const Table = tw.table`min-w-full bg-white rounded-lg shadow-md`;
const Th = tw.th`py-3 px-4 bg-primary-500 text-white font-semibold text-left`;
const Td = tw.td`py-2 px-4 border-b border-gray-200 text-gray-700`;
const Tr = tw.tr`hover:bg-gray-100 transition-colors`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;
const StatusBadge = styled.span(({ type }) => [
  tw`px-3 py-1 rounded-full text-xs font-bold`,
  type === "income" && tw`bg-green-100 text-green-800`,
  type === "outcome" && tw`bg-red-100 text-red-800`,
]);
const Button = tw.button`ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition`;
const ButtonGreen = tw.button`ml-2 px-3 py-1 rounded bg-green-500 text-white font-semibold hover:bg-green-700 transition`;
const LinkText = tw.span`
  text-blue-600 cursor-pointer border-b-2 border-blue-500 hover:text-blue-800 font-semibold transition ml-2 px-1
`;

const initialForm = {
  date: "",
  category: "",
  type: "income",
  amount: "",
  description: "",
  reference: "",
  neighborhood: ""
};

const AdminAccountingPage = () => {
  const { operations, loading, fetchOperations, createOperation, updateOperation, deleteOperation, autoCalculateIncome, periods, createPeriod, updatePeriod, archivePeriod } = useAccounting();
  const { actions: appActions } = useAppContext();
  // Periodos Contables modal state
  const [periodModalOpen, setPeriodModalOpen] = useState(false);
  const [periodEditMode, setPeriodEditMode] = useState(false);
  const [periodForm, setPeriodForm] = useState({ startDate: "", endDate: "" });

  const openCreatePeriodModal = () => {
    setPeriodForm({ startDate: "", endDate: "" });
    setPeriodEditMode(false);
    setPeriodModalOpen(true);
  };
  const closePeriodModal = () => {
    setPeriodModalOpen(false);
    setTimeout(() => {
      setPeriodForm({ startDate: "", endDate: "" });
    }, 200);
  };
  const handlePeriodChange = (e) => {
    setPeriodForm({ ...periodForm, [e.target.name]: e.target.value });
  };
  const handlePeriodSubmit = async (e) => {
    //on delete we need to move to status removed all records created for maintenance and rest of income and outcome
    //on create we need to assign maintance payments for all houses in the coto to the new period
    e.preventDefault();
    await createPeriod(periodForm);
    closePeriodModal();
  };

  const handleCloseOpenPeriod = async (period, status) => {
    if (window.confirm("¿Seguro que deseas cerrar este periodo contable?")) {
      await updatePeriod({ ...period, status: status });
    }
  };
  const handlePeriodArchive = async (id) => {
    if (window.confirm("¿Seguro que deseas archivar este periodo contable?")) {
      await archivePeriod(id);
    }
  };
  const { getCategoryOptionsById } = useCatalogs();
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [catalogCategoriesAuto, setCatalogCategoriesAuto] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [dateError, setDateError] = useState(false);
  const [selectedOp, setSelectedOp] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { fraccs } = useFraccs();
  const [selectedDateStart, setSelectedDateStart] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);
  // Dropdown for open periods
  const openPeriods = [{ id: "", startDate: "Selecciona", endDate: "Periodo", status: "" }].concat(periods.filter(p => p.status === "open"));
  const [selectedOpenPeriod, setSelectedOpenPeriod] = useState(openPeriods.length > 0 ? openPeriods[0].id : "");
  // Dropdown for period status filter
  const [periodStatusFilter, setPeriodStatusFilter] = useState("all");

  // Sync date range with selected open period ONLY if a period is selected (not default)
  useEffect(() => {
    // If default option selected, clear dates
    if(selectedOpenPeriod === "Selecciona - Periodo" || selectedOpenPeriod === ""){
      if (selectedDateStart !== null) setSelectedDateStart(null);
      if (selectedDateEnd !== null) setSelectedDateEnd(null);
      return;
    }

    const period = periods.find(p => p.id.toString() === selectedOpenPeriod.toString());
    if (period) {
      const newStart = period.startDate ? moment(period.startDate, 'YYYY-MM-DD') : null;
      const newEnd = period.endDate ? moment(period.endDate, 'YYYY-MM-DD') : null;
      if (
        (newStart && (!selectedDateStart || !selectedDateStart?.isSame(newStart, 'day')))
        || (!newStart && selectedDateStart)
      ) {
        setSelectedDateStart(newStart);
      }
      if (
        (newEnd && (!selectedDateEnd || !selectedDateEnd?.isSame(newEnd, 'day')))
        || (!newEnd && selectedDateEnd)
      ) {
        setSelectedDateEnd(newEnd);
      }
    }
  }, [selectedOpenPeriod, periods]);
  const [isAutoCalculating, setIsAutoCalculating] = useState(false);

  useEffect(() => { fetchOperations(); }, []);

  const openCreateModal = () => {
    setForm(initialForm);
    setEditMode(false);
    setModalOpen(true);
    setSelectedOp(null);
  };
  const openEditModal = (op) => {
    setForm({ ...op, amount: op.amount.toString() });
    setEditMode(true);
    setModalOpen(true);
    setSelectedOp(op);
  };
  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setForm(initialForm);
      setSelectedOp(null);
    }, 200);
  };
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setDateError(false);
    // Validate date is within an open period
    const openPeriod = periods.find(p => p.status === "open" && form.date >= p.startDate && form.date <= p.endDate);
    if (!openPeriod) {
      setDateError(true);
      appActions.addNotification("La fecha seleccionada está en un periodo cerrado", "error", 5000);
      return;
    }
    let op = { ...form, amount: Number(form.amount) };

    if (isAutoCalculating) {
      // Simulate API call to calculate amount
      const calculatedAmount = await autoCalculateIncome({
        category: form.category,
        date: form.date,
        neighborhood: form.neighborhood
      });
      op = { ...op, amount: calculatedAmount };
    }

    if (editMode) {
      await updateOperation({ ...selectedOp, ...op });
    } else {
      await createOperation(op);
    }
    closeModal();
  };
  const handleDelete = async (id) => {
    if (window.confirm("¿Seguro que deseas eliminar esta operación?")) {
      await deleteOperation(id);
    }
  };

  const handleOnClickButtons = (isAuto) => {
    setIsAutoCalculating(isAuto);
    openCreateModal();
  };

  // Filtering logic moved to useEffect below

  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    //catalogs section
    const loadCatalogs = () => {
      const catalog1 = getCategoryOptionsById(1);
      const catalog2 = getCategoryOptionsById(2);
      setCatalogCategories(catalog1);
      setCatalogCategoriesAuto(catalog2);
    };

    loadCatalogs();

    let filteredOps = operations;
    // Filter by period status (open, closed, archive)
    if (periodStatusFilter !== "all") {
      const periodIds = periods.filter(p => p.status === periodStatusFilter).map(p => p.id);
      filteredOps = filteredOps.filter(o => {
        const period = periods.find(p => o.date >= p.startDate && o.date <= p.endDate);
        return period && periodIds.includes(period.id);
      });
    }
    // Filter by type
    if (typeFilter !== "all") filteredOps = filteredOps.filter(o => o.type === typeFilter);
    // Filter by search
    if (search) {
      const s = search.toLowerCase();
      filteredOps = filteredOps.filter(o =>
        o.category.toLowerCase().includes(s) ||
        o.description.toLowerCase().includes(s) ||
        o.reference.toLowerCase().includes(s) ||
        o.neighborhood.toLowerCase().includes(s)
      );
    }
    // Filter by date range (robust to moment or string)
    const startMoment = selectedDateStart && moment.isMoment(selectedDateStart)
      ? selectedDateStart
      : selectedDateStart ? moment(selectedDateStart, 'YYYY-MM-DD') : null;
    const endMoment = selectedDateEnd && moment.isMoment(selectedDateEnd)
      ? selectedDateEnd
      : selectedDateEnd ? moment(selectedDateEnd, 'YYYY-MM-DD') : null;
    if (startMoment && endMoment) {
      filteredOps = filteredOps.filter(o => {
        const d = moment(o.date, 'YYYY-MM-DD');
        return d.isSameOrAfter(startMoment, 'day') && d.isSameOrBefore(endMoment, 'day');
      });
    } else if (startMoment) {
      filteredOps = filteredOps.filter(o => {
        const d = moment(o.date, 'YYYY-MM-DD');
        return d.isSameOrAfter(startMoment, 'day');
      });
    } else if (endMoment) {
      filteredOps = filteredOps.filter(o => {
        const d = moment(o.date, 'YYYY-MM-DD');
        return d.isSameOrBefore(endMoment, 'day');
      });
    }
    // Only update if different to avoid infinite loop
    if (
      filtered.length !== filteredOps.length ||
      filtered.some((item, idx) => item !== filteredOps[idx])
    ) {
      setFiltered(filteredOps);
    }
  }, [operations, periods, periodStatusFilter, typeFilter, search, selectedDateStart, selectedDateEnd]);

  // State for report modal
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTableData, setReportTableData] = useState([]);

  // Pagination state for operations table
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Helper: generate summary table data for the selected period
  const generateReportTableData = () => {
    // Find selected period
    const period = openPeriods.find(p => p.id.toString() === selectedOpenPeriod.toString());
    if (!period) return [];
    // Filter operations for this period
    const ops = operations.filter(o => o.date >= period.startDate && o.date <= period.endDate);
    // Group by category
    const summary = {};
    ops.forEach(o => {
      if (!summary[o.category]) {
        summary[o.category] = { category: o.category, income: 0, outcome: 0, balance: 0 };
      }
      if (o.type === "income") {
        summary[o.category].income += Number(o.amount);
        summary[o.category].balance += Number(o.amount);
      } else if (o.type === "outcome") {
        summary[o.category].outcome += Number(o.amount);
        summary[o.category].balance -= Number(o.amount);
      }
    });
    // Convert to array and add TOTAL row
    const arr = Object.values(summary);
    if (arr.length > 0) {
      const total = arr.reduce((acc, row) => {
        acc.income += row.income;
        acc.outcome += row.outcome;
        acc.balance += row.balance;
        return acc;
      }, { category: "TOTAL:", income: 0, outcome: 0, balance: 0 });
      arr.push(total);
    }
    // Format numbers as currency
    arr.forEach(row => {
      row.income = row.income ? `$${row.income.toLocaleString()}` : "";
      row.outcome = row.outcome ? `$${row.outcome.toLocaleString()}` : "";
      row.balance = row.balance ? `$${row.balance.toLocaleString()}` : "";
    });
    return arr;
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12 mb-12">
        <Heading>Contabilidad del Fraccionamiento</Heading>
        <div className="mt-12 flex flex-col items-center mb-12">
          <h2 className="text-2xl font-bold mb-2 text-center">Periodos Contables</h2>
          <p className="mb-4 text-center text-gray-700 max-w-2xl">
            Cada periodo representa un mes o ciclo de cobranza. Al abrir un nuevo periodo, el sistema asigna automáticamente las cuotas de mantenimiento a todos los residentes según su coto. Cierra el periodo cuando el ciclo concluya para generar el reporte de balance.
          </p>
          <div className="w-full max-w-2xl flex justify-end mb-2">
            <Button onClick={openCreatePeriodModal}>Nuevo Periodo</Button>
          </div>
          <div className="w-full max-w-2xl bg-white rounded-lg shadow-md overflow-hidden">
            <PeriodsTable
              periods={periods}
              handleCloseOpenPeriod={handleCloseOpenPeriod}
              handlePeriodArchive={handlePeriodArchive}
            />
          </div>
        </div>

        {/* Periodos Contables Modal */}
        {periodModalOpen && (
          <Dialog open={periodModalOpen} onClose={closePeriodModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">{periodEditMode ? "Cerrar Periodo" : "Abrir Nuevo Periodo"}</DialogTitle>
              <form onSubmit={handlePeriodSubmit}>
                <div className="mb-4">
                  <label htmlFor="period-startDate" className="block mb-1 font-semibold">Fecha de Inicio</label>
                  <input id="period-startDate" name="startDate" type="date" value={periodForm.startDate} onChange={handlePeriodChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-6">
                  <label htmlFor="period-endDate" className="block mb-1 font-semibold">Fecha de Fin</label>
                  <input id="period-endDate" name="endDate" type="date" value={periodForm.endDate} onChange={handlePeriodChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" onClick={closePeriodModal} className="bg-gray-400 hover:bg-gray-600">Cancelar</Button>
                  <Button type="submit">{periodEditMode ? "Cerrar Periodo" : "Abrir Periodo"}</Button>
                </div>
              </form>
            </div>
          </Dialog>
        )}

        <h2 className="text-2xl font-bold text-center">Balance General</h2>
        {/* Unified Card for Filters, Operations, Report, and Table */}
        <div className="flex justify-center my-8">
          <div className="w-full max-w-6xl bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-8 flex flex-col gap-8">
            {/* Filters Section */}
            <AccountingFilters
              openPeriods={openPeriods}
              selectedOpenPeriod={selectedOpenPeriod}
              setSelectedOpenPeriod={setSelectedOpenPeriod}
              periodStatusFilter={periodStatusFilter}
              setPeriodStatusFilter={setPeriodStatusFilter}
              selectedDateStart={selectedDateStart}
              setSelectedDateStart={setSelectedDateStart}
              selectedDateEnd={selectedDateEnd}
              setSelectedDateEnd={setSelectedDateEnd}
            />
            {/* Divider */}
            <div className="border-t border-blue-200 my-2" />
            {/* Operations Section */}
            <AccountingOperations
              search={search}
              setSearch={setSearch}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              handleOnClickButtons={handleOnClickButtons}
              Button={Button}
              ButtonGreen={ButtonGreen}
            />
            {/* Divider */}
            <div className="border-t border-blue-200 my-2" />
            {/* Report Section */}
            <AccountingReport
              openPeriods={openPeriods}
              selectedOpenPeriod={selectedOpenPeriod}
              setSelectedOpenPeriod={setSelectedOpenPeriod}
              operations={operations}
              reportModalOpen={reportModalOpen}
              setReportModalOpen={setReportModalOpen}
              reportTableData={reportTableData}
              setReportTableData={setReportTableData}
              generateReportTableData={generateReportTableData}
              Button={Button}
            />
            {/* Divider */}
            <div className="border-t border-blue-200 my-2" />
            {/* Table Section - moved inside the card, no extra margin */}
            <div className="overflow-x-auto">
              <OperationsTable
                loading={loading}
                filtered={filtered}
                openEditModal={openEditModal}
                handleDelete={handleDelete}
                Tr={Tr}
                Td={Td}
                Th={Th}
                StatusBadge={StatusBadge}
                LinkText={LinkText}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
              />
            </div>
          </div>
        </div>

        {/* Modal for add/edit */}
        {modalOpen && (
          (() => {
            let dialogTitle;
            if (editMode) {
              dialogTitle = "Editar Operación";
            } else if (isAutoCalculating) {
              dialogTitle = "Auto Calcular - Al dia de hoy";
            } else {
              dialogTitle = "Nueva Operación";
            }
            return (
              <Dialog open={modalOpen} onClose={closeModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
                <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-2xl z-50 relative">
                  <DialogTitle className="text-xl font-bold mb-4">{dialogTitle}</DialogTitle>
                  <AdminAccountingForm
                    handleSubmit={handleSubmit}
                    handleChange={handleChange}
                    form={form}
                    editMode={editMode}
                    closeModal={closeModal}
                    fraccs={isAutoCalculating ?
                      fraccs.filter(f => f.name !== "Fraccionamiento General").map(f => f.name)
                      : fraccs.map(f => f.name)
                    }
                    isAutoCalculating={isAutoCalculating}
                    categoryOptions={
                      isAutoCalculating
                        ? catalogCategoriesAuto.map(c => c.name)
                        : catalogCategories.map(c => c.name)
                    }
                    dateError={dateError}
                  />
                </div>
              </Dialog>
            );
          })()
        )}
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminAccountingPage;
